import * as yup from "yup";

export const REQUIRED_FIELD_MESSAGE = "Campo obligatorio";
const TYPE_ERROR_MESSAGE = "Dato invalido";
export const DATE_OUT_OF_RANGE_MESSAGE = "Fecha invalida";
const PERCENTAGE_NOT_ZERO_MESSAGE = "El porcentaje debe ser mayor al 0 %";
const PERCENTAGE_OUT_OF_RANGE_MESSAGE = "El porcentaje no puede superar al 100 %";
const INVALID_FORMAT_MESSAGE = "Formato invalido";
const PASSWORD_POLICIES = "La contrasena ingresada no respeta los criterios de contrasena";
const noAreaCodePhoneRegex = /^[0-9]*$/;
const areaCodeRegex = /^[0-9]*$/;

export const RequiredSchema = yup.mixed().required(REQUIRED_FIELD_MESSAGE);

export const RequiredStringSchema = yup
  .string()
  .required(REQUIRED_FIELD_MESSAGE)
  .min(1, REQUIRED_FIELD_MESSAGE);

export const RequiredNotEmptyStringSchema = yup
  .string()
  .trim()
  .typeError(REQUIRED_FIELD_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE);

export const RequiredPositiveNumberSchema = yup
  .number()
  .typeError(TYPE_ERROR_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE)
  .moreThan(999, INVALID_FORMAT_MESSAGE)
  .max(9999, INVALID_FORMAT_MESSAGE);

export const RequiredPercentSchema = yup
  .number()
  .typeError(REQUIRED_FIELD_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE)
  .positive(PERCENTAGE_NOT_ZERO_MESSAGE)
  .moreThan(0, PERCENTAGE_NOT_ZERO_MESSAGE)
  .max(100, PERCENTAGE_OUT_OF_RANGE_MESSAGE);

export const RequiredMailSchema = yup
  .string()
  .trim()
  .required(REQUIRED_FIELD_MESSAGE)
  .email("Formato de mail incorrecto");

export const MailSchema = yup.string().trim().email("Formato de mail incorrecto");

export const RequiredPhoneSchema = yup
  .string()
  .required(REQUIRED_FIELD_MESSAGE)
  .matches(noAreaCodePhoneRegex, "Verifique telefono");

export const PhoneSchema = yup
  .string()
  .matches(noAreaCodePhoneRegex, "Verifique telefono");

export const RequiredAreaCodeSchema = yup
  .string()
  .required(REQUIRED_FIELD_MESSAGE)
  .matches(areaCodeRegex, "Verifique codigo de area")
  .matches(/^[1-9][0-9]*$/, "El codigo de area no puede comenzar con 0");

export const AreaCodeSchema = yup
  .string()
  .matches(areaCodeRegex, "Verifique codigo de area");

export const RequiredWebSchema = yup
  .string()
  .matches(
    /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm,
    "Verifique formato URL"
  );

export const NotRequiredWebSchema = (field: string) =>
  yup
    .string()
    .notRequired()
    .nullable()
    .when(field, {
      is: (value: string) => value,
      then: (rule) =>
        rule.matches(
          /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[nNa-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm,
          "Verifique formato URL"
        ),
    });

export const RequiredSelectSchema = yup
  .number()
  .integer(REQUIRED_FIELD_MESSAGE)
  .positive(REQUIRED_FIELD_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE);

export const RequiredMultipleSelectSchema = yup
  .array()
  .min(1, REQUIRED_FIELD_MESSAGE)
  .of(yup.number().required(REQUIRED_FIELD_MESSAGE))
  .required(REQUIRED_FIELD_MESSAGE);

export const RequiredNumberSchema = yup
  .number()
  .required(REQUIRED_FIELD_MESSAGE)
  .typeError(TYPE_ERROR_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE);

export const NotRequiredNumberSchema = yup.lazy((value) =>
  value === "" ? yup.string() : yup.number().typeError(TYPE_ERROR_MESSAGE)
);

export const RequiredDayMonthSchema = yup
  .string()
  .required(REQUIRED_FIELD_MESSAGE)
  .matches(/([1-9]|[0-3][0-9])\/([0-1][0-9]|[1-9])/g, TYPE_ERROR_MESSAGE)
  .test("date", TYPE_ERROR_MESSAGE, (date) => {
    const [day, month] = (date?.split("/") || ["0", "0"]).map((x) => parseInt(x, 10));
    return day >= 1 && day <= 31 && month >= 1 && month <= 12;
  });

export const RequiredDaySchema = yup
  .string()
  .required(REQUIRED_FIELD_MESSAGE)
  .test("day", TYPE_ERROR_MESSAGE, (day) => {
    const dayParsed = day && parseInt(day, 10);
    if (!dayParsed) return false;
    return dayParsed >= 1 && dayParsed <= 31;
  });

export const RequiredMonthSchema = yup
  .string()
  .required(REQUIRED_FIELD_MESSAGE)
  .test("month", TYPE_ERROR_MESSAGE, (month) => {
    const monthParsed = month && parseInt(month, 10);
    if (!monthParsed) return false;
    return monthParsed >= 1 && monthParsed <= 12;
  });

export const RequiredPasswordSchema = yup
  .string()
  .required(REQUIRED_FIELD_MESSAGE)
  .test("policies", PASSWORD_POLICIES, (password) => {
    const regexUpper = /^(?=.*[A-Z]).*$/;
    const regexLower = /^(?=.*[a-z]).*$/;
    const regexNumber = /^(?=.*\d).*$/;
    const regexSpecial = /^(?=.*[!-?+$%_/~()=#{}@^*]).*$/;

    return Boolean(
      password &&
        password.length >= 8 &&
        regexUpper.test(password) &&
        regexLower.test(password) &&
        regexNumber.test(password) &&
        regexSpecial.test(password)
    );
  });

export const RequiredDateSchema = yup
  .date()
  .required(REQUIRED_FIELD_MESSAGE)
  .typeError(REQUIRED_FIELD_MESSAGE);

export const RequiredDateRangeSchema = (min: Date, max: Date) =>
  yup
    .date()
    .required(REQUIRED_FIELD_MESSAGE)
    .typeError(REQUIRED_FIELD_MESSAGE)
    .min(min, DATE_OUT_OF_RANGE_MESSAGE)
    .max(max, DATE_OUT_OF_RANGE_MESSAGE);
